import { Component, ReactNode } from 'react';
import { ErrorCard } from './ByxCard';

type BoundaryProps = {
  fallback: ReactNode;
  /** When it changes, a failed boundary tries its children again. */
  resetKey?: string;
  children?: ReactNode;
};

type BoundaryState = { failed: boolean };

/**
 * Catches a render error below it and shows the fallback instead. The error
 * itself is never rendered or logged here: entity data may be in it.
 */
class Boundary extends Component<BoundaryProps, BoundaryState> {
  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  state: BoundaryState = { failed: false };

  componentDidUpdate(previous: BoundaryProps) {
    // Only a failed boundary resets: a working one keeps its children
    // mounted (CR6-4).
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  componentDidCatch() {
    // Handled by the fallback; nothing to report (SR-12).
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * Error boundary of every new card and tab content (SR-12): one hostile
 * value hides that card's body behind "Não foi possível carregar <card>."
 * instead of blanking the page. No stack trace and no raw error text.
 */
export function ByxCardBoundary(
  props: Readonly<{
    title: string;
    variant?: 'card' | 'table';
    children?: ReactNode;
  }>,
) {
  const { title, variant, children } = props;
  return (
    <Boundary fallback={<ErrorCard title={title} variant={variant} />}>
      {children}
    </Boundary>
  );
}

/**
 * The same boundary around a page section — the entity header or the tab
 * content region (S2-1) — with the section's own fallback. The fallback
 * must be a generic pt-BR message: never the error, never a stack trace.
 * A new `resetKey` (e.g. the Grafo's centre) lets the section recover from
 * a failure without remounting it while it works.
 */
export function ByxSectionBoundary(
  props: Readonly<{
    fallback: ReactNode;
    resetKey?: string;
    children?: ReactNode;
  }>,
) {
  return (
    <Boundary fallback={props.fallback} resetKey={props.resetKey}>
      {props.children}
    </Boundary>
  );
}
